import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Collapse, IconButton, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { billingTypeAbbr, formatMinutesAsHours } from '../utils/timeUtils';
import HoursByTypeTable, { deriveHoursColumns } from './HoursByTypeTable';

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: { marginBottom: theme.spacing(3) },
    [theme.breakpoints.up('lg')]: { marginBottom: theme.spacing(4) },
}));

const HeaderBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
}));

const HeaderTitle = styled(Typography)({
    flexGrow: 1,
    minWidth: 0,
    order: 1,
});

const HeaderMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    order: 2,
    [theme.breakpoints.down('sm')]: {
        order: 3,
        width: '100%',
    },
}));

const HeaderExpand = styled(IconButton)(({ theme }) => ({
    order: 3,
    [theme.breakpoints.down('sm')]: {
        order: 2,
    },
}));

const FlushCardContent = styled(CardContent)({
    paddingTop: 0,
});

const ClientHoursByAccountBox = ({ accountsWithTime, loading, accounts, month, title }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(true);

    const rows = accounts
        .map((account) => {
            const match = (accountsWithTime || []).find((r) => r._id.toString() === account._id.toString());
            return match
                ? {
                      key: match._id,
                      label: match.name,
                      onClick: () => navigate(`/account/${match.slug}`),
                      time: match.aggregatedTime?.monthly?.[month] || {},
                  }
                : null;
        })
        .filter(Boolean);

    const { types, totals } = deriveHoursColumns(rows);

    return (
        <StyledCard>
            <HeaderBar>
                <HeaderTitle variant="subtitle1" fontWeight="medium">{title}</HeaderTitle>
                <HeaderExpand size="small" onClick={() => setExpanded(!expanded)}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </HeaderExpand>
                {!loading && types.length > 0 && (
                    <HeaderMeta>
                        {types.map((type) => (
                            <Typography key={type} variant="body2" color="text.secondary">
                                {billingTypeAbbr(type)}: <Box component="span" color={totals[type] > 0 ? 'success.main' : 'text.primary'}>{formatMinutesAsHours(totals[type])}</Box>
                            </Typography>
                        ))}
                    </HeaderMeta>
                )}
            </HeaderBar>
            <Collapse in={expanded}>
                <FlushCardContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <HoursByTypeTable rows={rows} labelHeading="Associated clients" />
                    )}
                </FlushCardContent>
            </Collapse>
        </StyledCard>
    );
};

export default ClientHoursByAccountBox;
